/**
 * Client environment (Vite `VITE_*` variables), parsed once at a trust boundary (docs/design/08 §1.1).
 * Missing Supabase values are allowed so the sandbox and tests run without a backend; `hasBackend` says
 * whether sign-in and server calls are possible.
 */
import { DEFAULT_ART_CONFIG, type ArtConfig } from '@play/catalog';
import { z } from 'zod';

const EnvSchema = z.object({
  VITE_SUPABASE_URL: z.url().optional().catch(undefined),
  VITE_SUPABASE_PUBLISHABLE_KEY: z.string().min(10).optional().catch(undefined),
  VITE_API_ORIGIN: z.string().optional().catch(undefined),
  VITE_ART_MIRROR: z.string().optional().catch(undefined),
  VITE_ENABLE_SANDBOX: z.string().optional().catch(undefined),
});

const raw = EnvSchema.parse(import.meta.env);

/** Parsed client configuration. */
export const env = {
  supabaseUrl: raw.VITE_SUPABASE_URL ?? null,
  supabaseKey: raw.VITE_SUPABASE_PUBLISHABLE_KEY ?? null,
  /** Origin of the Worker; empty string = same origin as the page. */
  apiOrigin: (raw.VITE_API_ORIGIN ?? '').replace(/\/+$/, ''),
  sandbox: raw.VITE_ENABLE_SANDBOX === '1' || import.meta.env.DEV,
  /** Build SHA for the crash beacon (set by CI through `VITE_GIT_SHA`). */
  sha: typeof import.meta.env.VITE_GIT_SHA === 'string' ? import.meta.env.VITE_GIT_SHA : 'dev',
} as const;

/** Whether Supabase is configured (sign-in, decks, invites, Porodigy). */
export const hasBackend = env.supabaseUrl !== null && env.supabaseKey !== null;

/** Art sources configuration: the R2 mirror first, Riot's CDN as fallback (owner decision Q3). */
export const artConfig: ArtConfig = (() => {
  const mirror = raw.VITE_ART_MIRROR?.trim();
  if (mirror === 'none') return { ...DEFAULT_ART_CONFIG, mirrorBase: null };
  if (mirror) return { ...DEFAULT_ART_CONFIG, mirrorBase: mirror };
  return DEFAULT_ART_CONFIG;
})();

/** The rules tiers this build offers (server feature flags may still refuse one; tier 3 is private, Q1). */
export const ENABLED_TIERS = [1, 2] as const;
export type EnabledTier = (typeof ENABLED_TIERS)[number];
